import moment from "moment";

import AppContext from "../app/app-context";
import BaseComponent from "../app/base-component";
import ApiClient from "../api-client/api-client";

import GenericPage from "../app/generic-page/generic-page";
import Table from "../app/table/table";

import FilterButton from "../app/generic-page/buttons/filter-button";

import QuotationPopup from "../calculation/quotation-popup/quotation-popup";

import "../quote-management/quote-management-page.css";
import QuoteStateSelector from "../quote-management/quote-state-selector/quote-state-selector";
import QueryFilterPopup from "../app/table/filter-popup/query-filter-popup";
import TableActionReorder from "../app/table/actions/table-action-reorder";
import TableActionSave from "../app/table/actions/table-action-save";
import TableActionDelete from "../app/table/actions/table-action-delete";

class OrderManagementPage extends BaseComponent {
  static contextType = AppContext;

  constructor(props) {
    super(props);
    this.state = {
      quotePopupShown: false,
      filterPopupShown: false,
      quotes: null,
      selectedQuote: null,
      accounts: [],
    };
  }

  updateFilterQuery(filterQuery) {
    this.setStateAsync({
      filterQuery,
    }).then(this.refreshData.bind(this));
  }

  updateSortQuery(sortQuery) {
    this.setStateAsync({
      sortQuery,
    }).then(this.refreshData.bind(this));
  }

  componentDidMount() {
    this.getOrderStates()
      .then(() => this.getAccounts())
      .then(() => this.refreshData());
  }

  getOrderStates() {
    return this.setStateAsync({
      orderStates: [
        {
          tag: "ORDERED",
          label: "order_status_ordered",
        },
        {
          tag: "ACCEPTED",
          label: "order_status_accepted",
          theme: "green",
          disabled: !this.context.userHasClaim("Orders", "Edit")
        },
        {
          tag: "ARCHIVED",
          label: "order_status_archived",
          disabled: !this.context.userHasClaim("Orders", "Edit")
        },
      ],
    });
  }

  getAccounts() {
    if (this.context.userHasClaim('Accounts', 'View')) {
      return new ApiClient(this.context)
        .call(
          "GET",
          `/v1/Tenant/${this.context.theme.tenantId}/Account`,
          null,
          false,
          {},
          {
            Sorts: "name",
            Filters: "Active==true",
          }
        )
        .then((response) => {
          let accounts = response.json;

          return this.setStateAsync({
            accounts: accounts,
          });
        });
    }
    else {
      return this.setStateAsync({
        accounts: [
          this.context.user.account
        ],
      });
    }
  }

  refreshData() {
    var sortQuery = this.state.sortQuery || "";
    var filterQuery = this.state.filterQuery || "";

    return new ApiClient(this.context)
      .call(
        "GET",
        "/v1/Order",
        null,
        false,
        {},
        {
          Sorts: sortQuery,
          Filters: filterQuery,
        }
      )
      .then((response) => {
        if (response.ok) {
          let quotes = response.json;

          this.setStateAsync((state) => ({
            quotes: quotes,
          }));
        }
      });
  }

  reorder(quote) {
    this.toggleEditQuotePopup(quote, true);
  }

  downloadPdf(quote) {
    let tl = this.context.translate;

    if (!quote.pdfFile) {
      this.context.addNotification("error", tl('notifications_download_unavailable'));
      return;
    }

    this.context.addNotification("info", tl('notifications_download_started'));

    new ApiClient(this.context)
      .call("GET", quote.pdfFile.filePath)
      .then((response) => {
        this.apiResponseAsDownload(response, `${quote.number}.pdf`);
      });
  }

  toggleEditQuotePopup(quote, reorder = false) {
    this.setState((state) => ({
      quotePopupShown: !state.quotePopupShown,
      selectedQuote: reorder ? null : quote,
      reorderId: reorder ? quote.id : null,
    }));
  }

  toggleFilterPopup() {
    this.setState((state) => ({
      filterPopupShown: !state.filterPopupShown,
    }));
  }

  renderButtons() {
    return (
      <div>
        <FilterButton onClick={this.toggleFilterPopup.bind(this)} />
      </div>
    );
  }

  renderTable() {
    let tl = this.context.translate;
    let currencyFormatter = new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' });

    return (
      <Table
        mapping={{
          number: {
            label: tl('order_order_number'),
            sortKey: "Number",
          },
          account: {
            label: tl('general_client'),
            value: (quote) => quote.account.name,
            sortKey: "AccountName",
          },
          contact: {
            label: tl('general_contact'),
            value: (quote) => quote.contact.fullName,
            sortKey: "ContactName",
          },
          title: {
            label: tl('order_project_reference'),
            sortKey: "Title",
          },
          createdAt: {
            label: tl('general_date'),
            value: (quote) => moment(quote.order.createdAt).format("DD-MM-YYYY"),
            sortKey: "OrderCreatedAt",
          },
          price: {
            label: tl('general_price'),
            value: (quote) => currencyFormatter.format(quote.price),
            sortKey: "Price",
          },
          status: {
            label: tl('general_status'),
            value: (quote) => this.renderStatus(quote),
            sortKey: "OrderStatus",
          },
          actions: {
            label: tl('general_actions'),
            value: (quote) => this.renderActions(quote),
          },
        }}
        entries={this.state.quotes}
        onSelect={(quote) => this.toggleEditQuotePopup(quote)}
        sortQuery={this.state.sortQuery}
        updateSortQuery={this.updateSortQuery.bind(this)}
      />
    );
  }

  updateStatus(quote, status) {
    let apiClient = new ApiClient(this.context);

    apiClient
      .call("PUT", "/v1/Quote/" + quote.id + "/OrderStatus", { status: status })
      .then((result) => {
        if (result.ok) {
          if (status === "CANCELLED") {
            this.context.addNotification(
              "success",
              this.context.translate('notification_quote_deleted')
            );
          } else {
            this.context.addNotification(
              "success",
              this.context.translate('notification_order_saved_success')
            );
          }
        } else {
          throw result;
        }
      })
      .then(() => {
        this.refreshData();
      });
  }

  renderStatus(quote) {
    return (
      <QuoteStateSelector
        tl={this.context.translate}
        disabled={!this.context.userHasClaim("Orders", "Edit")}
        value={quote.order.status}
        mapping={this.state.orderStates}
        onUpdateStatus={this.updateStatus.bind(this, quote)}
      />
    );
  }

  renderActions(quote) {
    return (
      <div>
        <TableActionReorder onClick={this.reorder.bind(this, quote)} />
        <TableActionSave onClick={this.downloadPdf.bind(this, quote)} />
        {this.context.userHasClaim("Orders", "Delete") && <TableActionDelete
          onClick={this.updateStatus.bind(this, quote, "CANCELLED")}
        />}
      </div>
    );
  }

  render() {
    let tl = this.context.translate;
    return (
      <GenericPage
        className="quoteManagementPage"
        title={tl('general_orders')}
        buttons={this.renderButtons()}
        content={this.renderTable()}
      >
        {this.state.filterPopupShown && (
          <QueryFilterPopup
            onDismiss={this.toggleFilterPopup.bind(this)}
            filterQuery={this.state.filterQuery}
            updateFilterQuery={this.updateFilterQuery.bind(this)}
            mapping={{
              Number: {
                label: tl('quotes_quotes_number'),
              },
              AccountId: {
                label: tl('general_client'),
                options: this.state.accounts.map((a) => ({
                  label: a.name,
                  value: a.id,
                })),
              },
              Title: {
                label: tl('quotes_project_reference'),
              },
              OrderCreatedAt: {
                label: tl('general_date'),
              },
              OrderStatus: {
                label: tl('general_status'),
                options: this.state.orderStates.map((m) => ({
                  label: tl(m.label),
                  value: m.tag,
                })),
              },
              Active: null,
            }}
          />
        )}
        {this.state.quotePopupShown && (
          <QuotationPopup
            onDismiss={this.toggleEditQuotePopup.bind(this)}
            quote={this.state.selectedQuote}
            reorderId={this.state.reorderId}
            order={true}
          />
        )}
      </GenericPage>
    );
  }
}

export default OrderManagementPage;
